import clsx from 'clsx';
import { rigSilhouette, type SilhouetteRole, type SilhouetteShape, type SilhouetteTrailer, type SilhouetteTruck } from '../policies/rig-silhouette';

/** Brand colours for the parts every vehicle shares; a vehicle's own paint comes from its `color`. */
const ROLE_CLASSES: Record<SilhouetteRole, string> = {
  paint: 'stroke-fg/30 [stroke-width:0.6]',
  // Navy running gear and near-black tyres, lifted toward the text colour in dark mode so they don't vanish into the charcoal.
  chassis: 'fill-logo-navy dark:fill-[color-mix(in_srgb,var(--color-logo-navy)_55%,var(--color-fg))]',
  tyre: 'fill-black/85 dark:fill-[color-mix(in_srgb,var(--color-black)_60%,var(--color-fg))]',
  hub: 'fill-[color-mix(in_srgb,var(--color-warning)_45%,var(--color-surface))]',
  window: 'fill-[color-mix(in_srgb,var(--color-primary)_45%,var(--color-surface))]',
  container: 'fill-accent',
  tipper: 'fill-[color-mix(in_srgb,var(--color-warning)_70%,var(--color-surface))]',
  dropside: 'fill-[color-mix(in_srgb,var(--color-success)_60%,var(--color-surface))]',
  gap: 'fill-none stroke-muted [stroke-dasharray:2_2] [stroke-width:1]',
};

interface RigSilhouetteProps {
  truck: (SilhouetteTruck & { color?: string }) | null;
  trailer: (SilhouetteTrailer & { color?: string }) | null;
  className?: string;
  /** Read out in place of the drawing; omit when the registrations sit beside it. */
  label?: string;
}

function Shape({ shape, colors }: { shape: SilhouetteShape; colors: { truck?: string; trailer?: string } }) {
  const className = ROLE_CLASSES[shape.role];
  if (shape.kind === 'circle') return <circle className={className} cx={shape.cx} cy={shape.cy} r={shape.r} />;
  const fill = shape.paint ? colors[shape.paint] : undefined;
  const style = fill ? { fill } : undefined;
  if (shape.kind === 'polygon') return <polygon className={className} style={style} points={shape.points.map((point) => point.join(',')).join(' ')} />;
  return <rect className={className} style={style} x={shape.x} y={shape.y} width={shape.width} height={shape.height} rx={1} />;
}

/** A side view of a truck, a trailer, or the rig they make up (gitaumoses4/chukua#390), each in its own paint. */
export function RigSilhouette({ truck, trailer, className, label }: RigSilhouetteProps) {
  const drawing = rigSilhouette({ truck, trailer });
  const colors = { truck: truck?.color, trailer: trailer?.color };

  return (
    <svg
      viewBox={`0 0 ${drawing.width} ${drawing.height}`}
      preserveAspectRatio="xMinYMax meet"
      className={clsx('max-w-full', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <line className="stroke-line" x1={0} y1={44.5} x2={drawing.width} y2={44.5} />
      {drawing.shapes.map((shape, index) => (
        <Shape key={index} shape={shape} colors={colors} />
      ))}
    </svg>
  );
}
